/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 会员权益限制弹窗组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-04 18:01:17
 */
import React from 'react'
import { Modal } from 'antd'
import {
  CloseOutlined,
  CustomerServiceOutlined,
  LockOutlined
} from '@ant-design/icons'
import {
  useVip,
  usePayment,
  useAgentCustomerDrawer,
  useAppSelector
} from '../../../../../app/hooks'
import CONFIG from '../../../../../constants/config'
import sensors, {
  sensorsRole,
  sensorsVipLevel
} from '../../../../../utils/sensors'
import person_icon from '../../../../../images/crown-vip-icon.png'
import crown_vVip_icon from '../../../../../images/crown-vVip-icon.png'
import crown_svip_icon from '../../../../../images/crown-svip-icon.png'
import vipRightsImg from '../../../../../images/watermark-batch-editor/vip_rights.jpg'
import vvipRightsImg from '../../../../../images/watermark-batch-editor/vvip_rights.jpg'
import svipRightsImg from '../../../../../images/watermark-batch-editor/svip_rights.jpg'
import evipRightsImg from '../../../../../images/watermark-batch-editor/evip_rights.jpg'
import vvipRightsComparisonImg from '../../../../../images/watermark-batch-editor/vvip_rights_comparison.jpg'
import svipRightsComparisonImg from '../../../../../images/watermark-batch-editor/svip_rights_comparison.jpg'
import evipRightsComparisonImg from '../../../../../images/watermark-batch-editor/evip_rights_comparison.png'
import './index.scss'

/** 各等级权益图 */
const TIER_RIGHTS_IMAGES: Record<
  'vip' | 'vvip' | 'svip' | 'enterprise',
  string
> = {
  vip: vipRightsImg,
  vvip: vvipRightsImg,
  svip: svipRightsImg,
  enterprise: evipRightsImg
}

/** 各等级权益对比图 */
const TIER_RIGHTS_COMPARISON_IMAGES: Record<
  'vip' | 'vvip' | 'svip' | 'enterprise',
  string
> = {
  vip: vvipRightsComparisonImg,
  vvip: vvipRightsComparisonImg,
  svip: svipRightsComparisonImg,
  enterprise: evipRightsComparisonImg
}

interface MembershipTierLimitsModalProps {
  visible: boolean
  photoCount: number
  onCancel: () => void
  onConfirm?: (limit: number) => void // 确认继续执行的回调，接收限制数量
}

/**
 * 会员权益限制弹窗组件
 * 根据用户会员等级和选择的图片数量，显示相应的限制提示
 */
const MembershipTierLimitsModal: React.FC<MembershipTierLimitsModalProps> = ({
  visible,
  photoCount,
  onCancel,
  onConfirm
}) => {
  const payment = usePayment()
  const customerAgentDrawer = useAgentCustomerDrawer()

  // 从 Redux state 获取 vip_kind
  const group = useAppSelector((state) => state.group)
  const vipKind = group.vipInfo?.vip_kind
  const isVip = group.isVip
  const groupID = group.groupId
  const myRole = group.myRole

  const isOver100 = photoCount > 100

  // 选中状态管理，超过100张时默认选中企业会员，否则默认选中超级会员
  const [selectedTier, setSelectedTier] = React.useState<
    'vip' | 'vvip' | 'svip' | 'enterprise'
  >(isOver100 ? 'enterprise' : 'svip')

  // 当照片数量变化时，更新默认选中项
  React.useEffect(() => {
    if (isOver100) {
      setSelectedTier('enterprise')
    } else {
      setSelectedTier('svip')
    }
  }, [isOver100])

  // 获取当前会员等级信息 - 根据 vip_kind 判断
  const currentMembershipInfo = React.useMemo(() => {
    if (!isVip) {
      return {
        tier: '非会员',
        limit: 0
      }
    }

    switch (vipKind) {
      case CONFIG.VIP_KIND.SVIP: // 2 - 超级会员
        return {
          tier: '超级会员',
          limit: 100
        }
      case CONFIG.VIP_KIND.VVIP: // 3 - 高级会员
        return {
          tier: '高级会员',
          limit: 20
        }
      case CONFIG.VIP_KIND.VIP: // 1 - 基础会员
        return {
          tier: '基础会员',
          limit: 1
        }
      default:
        return {
          tier: '非会员',
          limit: 0
        }
    }
  }, [vipKind])

  // 埋点函数
  const trackEvent = React.useCallback(
    (actionType: string, extraParams?: Record<string, any>) => {
      sensors.track('workgroup_b7748_click', {
        requirement_id: 513,
        track_sign: 'aworkgroup.b7748.click.workgroup_b7748_click',
        actionType,
        pageType: 'batchEditorWatermark',
        groupID: groupID || '',
        vipLevel: sensorsVipLevel(group),
        role: sensorsRole(myRole),
        photoNum: photoCount,
        ...extraParams
      })
    },
    [groupID, myRole, photoCount]
  )

  // 弹窗显示时埋点
  React.useEffect(() => {
    if (visible) {
      trackEvent('showMembershipLimitModal', {
        number1: currentMembershipInfo.limit // 限制数量
      })
    }
  }, [visible, trackEvent, vipKind, isOver100])

  // 处理升级会员
  const handleUpgrade = () => {
    // 埋点：点击立即升级
    trackEvent('clickUpgradeMembership', {
      number1: currentMembershipInfo.limit // 限制数量
    })

    // 根据用户选中的会员等级打开对应的支付弹窗
    if (selectedTier === 'enterprise') {
      // 选中企业会员，联系客服
      handleContactService()
    } else if (selectedTier === 'svip') {
      // 选中超级会员，打开超级会员支付弹窗
      // openSvip(sourceDetail, isFromToView, onlySvip, onlyVVip)
      // onlySvip=true 表示只显示超级会员，定位到超级会员
      payment.openSvip('webBatchEditWatermark', false, true, false)
    } else if (selectedTier === 'vvip') {
      // 选中高级会员，打开高级会员支付弹窗
      // onlyVVip=true 表示只显示高级会员，定位到高级会员
      payment.openSvip('webBatchEditWatermark', false, false, true)
    } else {
      // 选中基础会员，打开基础会员支付弹窗
      payment.open('webBatchEditWatermark')
    }
    // 关闭弹窗
    onCancel()
  }

  // 处理联系客服
  const handleContactService = () => {
    // 埋点：点击联系客服
    trackEvent('clickContactService')
    // 打开客服对话框
    customerAgentDrawer.open({ fromPage: 'batchEditorWatermark' })
  }

  // 处理取消/关闭
  const handleCancel = () => {
    // 埋点：点击暂不升级/关闭
    trackEvent('clickCancelUpgrade')
    onCancel()
  }

  // 选中等级对应的展示名（用于按钮文案）
  const selectedTierLabel =
    selectedTier === 'vip'
      ? '基础会员'
      : selectedTier === 'vvip'
        ? '高级会员'
        : selectedTier === 'svip'
          ? '超级会员'
          : '企业版'

  // 处理确认继续执行（处理前*张）
  const handleConfirm = () => {
    // 埋点：点击处理前*张
    trackEvent('clickProcessLimitedPhotos', {
      number1: currentMembershipInfo.limit // 限制数量
    })
    // 关闭弹窗并继续执行水印应用，传递限制数量
    onCancel()
    if (onConfirm) {
      onConfirm(currentMembershipInfo.limit)
    }
  }

  return (
    <Modal
      open={visible}
      title={null}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      mask={{ closable: true }}
      keyboard={true}
      width={1000}
      className="membership-tier-limits-modal"
      centered
    >
      <div className="membership-tier-limits-content">
        <div className="membership-tier-limits-header">
          <div className="header-left">
            <h3 className="header-title">开通会员，解锁更多每次编辑张数</h3>
            <div className="header-promo">
              <span className="header-promo-text">
                一人开通，全团队共享权益
              </span>
              <span className="header-promo-dot" aria-hidden="true">
                ·
              </span>
              <span className="header-promo-highlight">
                升级即享更高单次编辑张数
              </span>
            </div>
          </div>
          <div className="header-right">
            <div className="header-count">
              本次已选 <strong>{photoCount + 1}</strong> 张
            </div>
            <button
              type="button"
              className="header-close"
              onClick={handleCancel}
              aria-label="关闭"
            >
              <CloseOutlined />
            </button>
          </div>
        </div>
        <div className="membership-tier-limits-columns">
          {/* 左侧：您将获得（居左，不在灰底内）+ 等级权益图 + 权益对比图（可滑动查看） */}
          <div className="membership-tier-limits-left">
            <div className="right-header">
              <div className="right-header-text">
                <h5 className="right-title">您将获得超多权益</h5>
              </div>
            </div>
            <div className="membership-tier-limits-left-inner">
              <div className="tier-rights-images-scroll">
                <div className="tier-rights-image-wrap">
                  <img
                    src={TIER_RIGHTS_IMAGES[selectedTier]}
                    alt={`${selectedTier}权益`}
                    className="tier-rights-image"
                  />
                </div>
                <div className="tier-rights-image-wrap tier-rights-comparison-wrap">
                  <img
                    src={TIER_RIGHTS_COMPARISON_IMAGES[selectedTier]}
                    alt="权益对比"
                    className="tier-rights-image"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* 右侧：会员等级卡片 */}
          <div className="membership-tier-limits-right">
            <div className="right-header">
              <h5 className="right-title">选择方案</h5>

              <div className="header-subtitle header-subtitle-inline">
                <LockOutlined className="header-subtitle-icon" />
                <span className="header-subtitle-text">
                  <span className="header-subtitle-label">当前为</span>
                  <span className="header-subtitle-value header-subtitle-tier">
                    {currentMembershipInfo.tier}
                  </span>
                  <span className="header-subtitle-label">
                    {' '}
                    · 每次编辑上限{' '}
                  </span>
                  <span className="header-subtitle-value header-subtitle-limit">
                    {currentMembershipInfo.limit}张
                  </span>
                </span>
              </div>
            </div>

            <div className="modal-body">
              <div className="membership-tiers">
                {/* 基础会员 */}
                <button
                  type="button"
                  className={`tier-item basic-tier ${
                    selectedTier === 'vip' ? 'selected' : ''
                  }`}
                  onClick={() => {
                    setSelectedTier('vip')
                    trackEvent('clickVip')
                  }}
                  aria-pressed={selectedTier === 'vip'}
                >
                  <div className="tier-info">
                    <div className="tier-icon person">
                      <img
                        src={person_icon}
                        width={30}
                        height={30}
                        alt="基础会员"
                      />
                    </div>
                    <div className="tier-details">
                      <h4 className="tier-name">基础会员</h4>
                    </div>
                  </div>
                  <div className="tier-limit">
                    <span className="limit-unit">{`每次编辑<=`}</span>
                    <span className="limit-number">1张</span>
                  </div>
                </button>

                {/* 高级会员 */}
                <button
                  type="button"
                  className={`tier-item advanced-tier ${
                    selectedTier === 'vvip' ? 'selected' : ''
                  }`}
                  onClick={() => {
                    setSelectedTier('vvip')
                    trackEvent('clickVVip')
                  }}
                  aria-pressed={selectedTier === 'vvip'}
                >
                  <div className="tier-info">
                    <div className="tier-icon stars">
                      <img
                        src={crown_vVip_icon}
                        width={30}
                        height={30}
                        alt="高级会员"
                      />
                    </div>
                    <div className="tier-details">
                      <h4 className="tier-name">高级会员</h4>
                    </div>
                  </div>
                  <div className="tier-limit">
                    <span className="limit-unit">{`每次编辑<=`}</span>
                    <span className="limit-number">20张</span>
                  </div>
                </button>

                {/* 超级会员 */}
                <button
                  type="button"
                  className={`tier-item super-tier ${
                    selectedTier === 'svip' ? 'selected' : ''
                  }`}
                  onClick={() => {
                    setSelectedTier('svip')
                    trackEvent('clickSVip')
                  }}
                  aria-pressed={selectedTier === 'svip'}
                >
                  <div className="tier-info">
                    <div className="tier-icon diamond">
                      <img
                        src={crown_svip_icon}
                        width={35}
                        height={35}
                        alt="超级会员"
                      />
                    </div>
                    <div className="tier-details">
                      <div className="tier-name-row">
                        <h4 className="tier-name">超级会员</h4>
                        <span className="recommended-badge">推荐</span>
                      </div>
                      <span className="tier-highlight-tag">
                        支持上传到文件夹
                      </span>
                    </div>
                  </div>
                  <div className="tier-limit">
                    <span className="limit-unit">{`每次编辑<=`}</span>
                    <span className="limit-number">100张</span>
                  </div>
                </button>

                {/* 企业版：一行提示 */}
                <button
                  type="button"
                  className="enterprise-prompt-line"
                  onClick={() => {
                    handleContactService()
                    onCancel()
                  }}
                >
                  <CustomerServiceOutlined className="enterprise-prompt-icon" />
                  <span>
                    需要更多批量编辑张数？联系客服开通企业版，单次可处理 100+ 张
                  </span>
                </button>
              </div>
            </div>

            {/* 操作按钮：放在内容区右侧 */}
            <div className="membership-tier-limits-actions">
              {currentMembershipInfo.tier !== '非会员' && (
                <button
                  type="button"
                  className="footer-action-secondary"
                  onClick={handleConfirm}
                >
                  处理前{currentMembershipInfo.limit}张
                </button>
              )}
              <button
                type="button"
                className="footer-action-primary"
                onClick={handleUpgrade}
              >
                {selectedTier === 'enterprise'
                  ? '联系客服开通企业版'
                  : `升级${selectedTierLabel}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MembershipTierLimitsModal
